import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    // Defer downloading the video until it is near the viewport.
    // Previously every background video on the page started downloading on load,
    // which competed with the product image (LCP) on mobile connections.
    if (!('IntersectionObserver' in window)) {
      this.#loadVideo();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#observer?.disconnect();
          this.#observer = null;
          this.#loadVideo();
        }
      },
      { rootMargin: '0px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #loadVideo() {
    if (!this.isConnected || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Mobile gets the optimized poster only. Background video adds a large
    // transfer to the critical mobile load while contributing no LCP value.
    if (window.matchMedia('(max-width: 768px)').matches || window.innerWidth <= 768) return;
    // On data-saver / slow connections keep the poster image only — no video download.
    const connection = /** @type {any} */ (navigator).connection;
    if (connection && (connection.saveData || /(^|-)2g|3g/.test(connection.effectiveType || ''))) return;

    // Never compete with the initial page load (LCP image, CSS, fonts).
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.#loadVideo(), { once: true });
      return;
    }

    const { videoSources, videoElement } = this.refs;
    const chosen = this.#pickSource(videoSources);
    // Keep the poster on mobile when Shopify has no lightweight rendition.
    if (!chosen) return;

    for (const source of videoSources) {
      if (chosen && source !== chosen) {
        source.remove();
        continue;
      }
      const videoSource = source.dataset.videoSource || '';
      if (videoSource) source.setAttribute('src', videoSource);
    }

    // On mobile, play when idle to preserve smooth initial rendering
    const play = () => {
      if (!this.isConnected || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      videoElement.load();
      videoElement.play?.().catch(() => {});
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(play, { timeout: 1500 });
    } else {
      setTimeout(play, 300);
    }
  }

  /**
   * Picks the smallest suitable MP4 rendition (480p on mobile, 1080p max on desktop).
   * @param {HTMLElement[]} sources
   * @returns {HTMLElement | null}
   */
  #pickSource(sources) {
    const optimized = sources.find((source) => source.dataset.videoOptimized === 'true');
    if (optimized) return optimized;
    const isMobile = window.matchMedia('(max-width: 768px)').matches || window.innerWidth <= 768;
    const maxHeight = isMobile ? 480 : 1080;
    const mp4s = sources
      .filter((s) => s.getAttribute('type') === 'video/mp4')
      .sort((a, b) => (Number(a.dataset.videoHeight) || 0) - (Number(b.dataset.videoHeight) || 0));

    if (!mp4s.length) return isMobile ? null : sources[0] || null;

    const fitting = mp4s.filter((s) => {
      const h = Number(s.dataset.videoHeight);
      return h > 0 && h <= maxHeight;
    });
    return fitting.length ? fitting[fitting.length - 1] : isMobile ? null : mp4s[0];
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
